import { Component, signal, ChangeDetectionStrategy, HostListener, computed, effect, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { LayoutService } from '../../../core/services/layout.service';
import { HugeiconsIconComponent } from '@hugeicons/angular';
import { ArrowDown01Icon, LogoutSquare01Icon, Menu01Icon, Notification01Icon, Settings01Icon, User03Icon } from '@hugeicons/core-free-icons';
import { Select, SelectOption } from '../select/select';
import { AuthService } from '../../../core/services/auth/auth-service';
import { getAllowedRoutesForRole } from '../../../core/config/menu-config';
import { ApiService } from '../../../core/services/api/api-service';
import { requests } from '../../../core/config/config';
import { processSearchResponse } from '../../../core/utils/claim-select.utils';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, FormsModule, HugeiconsIconComponent, Select],
  templateUrl: './header.html',
  styleUrl: './header.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Header {
  // Expose the icon to the template
  // Note: SearchFocusIcon doesn't exist in Hugeicons, using Search01Icon instead
  // You can rename this property to match your preference
  ArrowDownIcon = ArrowDown01Icon;
  Notification01Icon = Notification01Icon;
  // derive user-related values from AuthService.user signal
  userName = computed(() => this.authService.user()?.userName ?? 'User');
  LogoutSquare01Icon = LogoutSquare01Icon;
  Settings01Icon = Settings01Icon;
  User03Icon = User03Icon;
  Menu01Icon = Menu01Icon;
  roleOptions = signal<SelectOption[]>([]);
  regionOptions = signal<SelectOption[]>([]);
  countryOptions = signal<SelectOption[]>([]);
  fallbackCountryOptions = signal<SelectOption[]>([]);
  fallbackCountriesLoading = signal(false);
  dmSiteOptions = signal<SelectOption[]>([]);
  dmSitesLoading = signal(false);
  private regionsWithCountries: Array<any> = [];
  private allCountryOptions: SelectOption[] = [];
  private fallbackCountriesRequestId = 0;

  isAdminRoleSelected = computed(() => {
    const selectedRole = this.selectedRole();
    if (selectedRole === null) return false;

    const selectedRoleOption = this.roleOptions().find(
      option => String(option.value) === String(selectedRole)
    ) ?? this.roleOptions()[0];

    if (!selectedRoleOption) return false;

    return String(selectedRoleOption.label).trim().toLowerCase() === 'admin';
  });

  useFallbackCountryDropdown = computed(() =>
    !this.isAdminRoleSelected() && this.regionUsesFallbackCountries(this.selectedRegion())
  );

  // store primitive value (string | number | null), not SelectOption object
  selectedRole = signal<string | number | null>(null);
  selectedRegion = signal<string | number | null>(null);
  selectedCountry = signal<string | number | null>(null);
  selectedDmSite = signal<string | number | null>(null);
  private selectedDmSiteDisplayName = signal<string | null>(null);

  showUserMenu = signal(false);
  showNotifications = signal(false);
  private isInitialLoad = true;
  /** Last role we reacted to, so navigation fires only on an actual role change. */
  private previousRole: string | number | null = null;

  constructor(
    private layoutService: LayoutService,
    private authService: AuthService,
    private router: Router,
    private apiService: ApiService
  ) {
    // Update selects when user data changes
    effect(() => {
      const user = this.authService.user();

      if (!user) {
        this.roleOptions.set([]);
        this.regionOptions.set([]);
        this.countryOptions.set([]);
        this.fallbackCountryOptions.set([]);
        this.fallbackCountriesLoading.set(false);
        this.selectedRole.set(null);
        this.selectedRegion.set(null);
        this.selectedCountry.set(null);
        this.clearDmSite();
        this.layoutService.setSelectedRole(null);
        this.layoutService.setSelectedRegion(null);
        this.layoutService.setSelectedCountry(null);
        return;
      }

      // Map roles -> role options
      const roles: SelectOption[] = (user.roles || []).map((r: any) => ({ value: r.id, label: r.name }));
      this.roleOptions.set(roles);

      // Map regions
      const regions: SelectOption[] = (user.regions || []).map((r: any) => ({ value: r.id, label: r.name }));
      this.regionOptions.set(regions);

      this.regionsWithCountries = Array.isArray(user.regions_with_countries) ? user.regions_with_countries : [];
      this.allCountryOptions = this.mapValidCountries(user.countries || []);

      // Set defaults to first available (store primitive value)
      const defaultRole = roles.length ? roles[0].value : null;
      const layoutRole = untracked(() => this.layoutService.selectedRole());
      const roleIds: (string | number)[] = (user.roles || []).map((r: any) => r.id);
      const validStoredRole = layoutRole !== null && roleIds.some(id => id === layoutRole || String(id) === String(layoutRole));
      const effectiveRole = validStoredRole ? layoutRole : defaultRole;
      this.selectedRole.set(effectiveRole);
      this.layoutService.setSelectedRole(effectiveRole);

      const layoutRegion = untracked(() => this.layoutService.selectedRegion());
      const validStoredRegion = layoutRegion !== null && regions.some(r => r.value === layoutRegion || String(r.value) === String(layoutRegion));
      const effectiveRegion = validStoredRegion ? layoutRegion : null;
      const normalizedEffectiveRegion = effectiveRegion === '' ? null : effectiveRegion;
      this.selectedRegion.set(normalizedEffectiveRegion);
      this.layoutService.setSelectedRegion(normalizedEffectiveRegion);

      const layoutCountry = untracked(() => this.layoutService.selectedCountry());
      const countries = this.getCountryOptionsForRegion(normalizedEffectiveRegion);
      this.countryOptions.set(countries);
      const validStoredCountry = layoutCountry !== null && countries.some(c => this.sameValue(c.value, layoutCountry));
      // const effectiveCountry = validStoredCountry ? layoutCountry : (countries[0]?.value ?? null);
      const effectiveCountry = validStoredCountry ? layoutCountry : null;
      this.selectedCountry.set(effectiveCountry === '' ? null : effectiveCountry);
      this.layoutService.setSelectedCountry(effectiveCountry === '' ? null : effectiveCountry);
      this.fetchDmSites(effectiveCountry === '' ? null : effectiveCountry, undefined, { autoSelectFirst: true });

      if (this.regionUsesFallbackCountries(normalizedEffectiveRegion)) {
        this.fetchFallbackCountries(normalizedEffectiveRegion);
      } else {
        this.fallbackCountryOptions.set([]);
        this.fallbackCountriesLoading.set(false);
      }
    });

    // Sync selectedRole changes to LayoutService and navigate to role-specific dashboard
    effect(() => {
      const selectedRole = this.selectedRole();
      this.layoutService.setSelectedRole(selectedRole);
      const region = this.selectedRegion();
      const country = this.selectedCountry();
      const dmSite = this.selectedDmSite();
      const normalizedRegion = region === '' ? null : region;
      const normalizedCountry = country === '' ? null : country;
      const normalizedDmSite = dmSite === '' ? null : dmSite;
      this.layoutService.setSelectedRegion(normalizedRegion);
      this.layoutService.setSelectedCountry(normalizedCountry);
      this.layoutService.setSelectedDmSite(normalizedDmSite);

      // Navigate only when the ROLE actually changes (region/country/DM-site are
      // filters on the same page and must never move the user). Keep the user on
      // their current page when it's still allowed for the new role; only fall back
      // to the role's landing page when the current page isn't permitted.
      const roleChanged = !this.isInitialLoad && selectedRole !== this.previousRole;
      if (roleChanged && selectedRole !== null) {
        const allowedRoutes = getAllowedRoutesForRole(selectedRole);
        const currentPath = this.router.url.split('?')[0];
        const currentStillAllowed = allowedRoutes.some(
          route => currentPath === route || currentPath.startsWith(route + '/')
        );
        if (!currentStillAllowed && allowedRoutes.length > 0) {
          this.router.navigate([allowedRoutes[0]]);
        }
      }

      this.previousRole = selectedRole;
      this.isInitialLoad = false;
    });
  }

  onRegionChange(regionId: string | number | null): void {
    const normalizedRegion = regionId === '' ? null : regionId;
    this.selectedRegion.set(normalizedRegion);
    this.layoutService.setSelectedRegion(normalizedRegion);

    const countries = this.getCountryOptionsForRegion(normalizedRegion);
    this.countryOptions.set(countries);

    if (this.regionUsesFallbackCountries(normalizedRegion)) {
      this.selectedCountry.set(null);
      this.layoutService.setSelectedCountry(null);
      this.fetchDmSites(null);
      this.fetchFallbackCountries(normalizedRegion);
      return;
    }

    this.fallbackCountryOptions.set([]);
    this.fallbackCountriesLoading.set(false);

    const nextCountry = countries[0]?.value ?? null;
    this.selectedCountry.set(nextCountry);
    this.layoutService.setSelectedCountry(nextCountry);
    this.selectedDmSite.set(null);
    this.layoutService.setSelectedDmSite(null);
    this.fetchDmSites(nextCountry, undefined, { autoSelectFirst: true });
  }

  onCountryChange(countryId: string | number | null): void {
    const normalizedCountry = countryId === '' ? null : countryId;
    this.selectedCountry.set(normalizedCountry);
    this.layoutService.setSelectedCountry(normalizedCountry);
    if (normalizedCountry == null) {
      this.fetchDmSites(null);
    } else {
      this.selectedDmSite.set(null);
      this.layoutService.setSelectedDmSite(null);
      this.fetchDmSites(normalizedCountry, undefined, { autoSelectFirst: true });
    }
  }

  onDmSiteChange(siteId: string | number | null): void {
    const normalizedSite = siteId === '' ? null : siteId;
    this.selectedDmSite.set(normalizedSite);
    this.layoutService.setSelectedDmSite(normalizedSite);
    if (normalizedSite == null) {
      this.selectedDmSiteDisplayName.set(null);
      return;
    }
    const match = this.dmSiteOptions().find((o) => this.sameValue(o.value, normalizedSite));
    this.selectedDmSiteDisplayName.set(match ? String(match.label) : null);
  }

  onDmSiteSearch = (query: string): void => {
    const countryId = this.selectedCountry();
    if (countryId == null) return;

    const q = (query || '').trim();
    if (!q) {
      if (this.selectedDmSite() != null) return;
      // Clear icon triggers onSearch(''); keep existing options and stay deselected (like country clear).
      if (this.dmSiteOptions().length > 0) return;
      this.fetchDmSites(countryId);
      return;
    }
    this.fetchDmSites(countryId, q);
  };

  onDmSiteDropdownOpened(): void {
    const countryId = this.selectedCountry();
    if (countryId == null) return;
    if (!this.dmSitesLoading() && this.dmSiteOptions().length === 0) {
      this.fetchDmSites(countryId);
    }
  }

  private clearDmSite(): void {
    this.dmSiteOptions.set([]);
    this.selectedDmSite.set(null);
    this.selectedDmSiteDisplayName.set(null);
    this.layoutService.setSelectedDmSite(null);
  }

  private fetchDmSites(
    countryId: string | number | null,
    searchQuery?: string,
    opts?: { autoSelectFirst?: boolean }
  ): void {
    if (countryId == null || countryId === '') {
      this.clearDmSite();
      return;
    }

    const payload: Record<string, unknown> = { country_id: this.toApiId(countryId) };
    if (searchQuery) {
      payload['code'] = searchQuery;
    } else {
      payload['page'] = 1;
      payload['limit'] = 10;
    }

    this.dmSitesLoading.set(true);
    this.apiService.sendRequest(requests.getAllDmSites, 'post', payload).subscribe({
      next: (res: any) => {
        const rows = this.extractDmSiteRows(res);
        const mapped: SelectOption[] = rows.map((s: any) => ({
          value: s.id ?? s.site_id,
          label: s.name ?? s.code ?? String(s.id ?? s.site_id),
        }));

        const layoutSite = untracked(() => this.layoutService.selectedDmSite());
        if (!searchQuery && layoutSite != null && this.selectedDmSite() == null) {
          this.selectedDmSite.set(layoutSite);
          const match = mapped.find((o) => this.sameValue(o.value, layoutSite));
          if (match) {
            this.selectedDmSiteDisplayName.set(String(match.label));
          }
        }

        const selectedId = this.selectedDmSite();
        const displayName = this.selectedDmSiteDisplayName();

        processSearchResponse(
          mapped,
          searchQuery,
          selectedId,
          displayName,
          (opts) => this.dmSiteOptions.set(opts),
          (updater) => this.dmSiteOptions.update(updater),
          () => {
            this.selectedDmSite.set(null);
            this.selectedDmSiteDisplayName.set(null);
            this.layoutService.setSelectedDmSite(null);
          }
        );

        if (opts?.autoSelectFirst && !searchQuery && this.selectedDmSite() == null && mapped.length > 0) {
          const first = mapped[0];
          this.selectedDmSite.set(first.value);
          this.selectedDmSiteDisplayName.set(String(first.label));
          this.layoutService.setSelectedDmSite(first.value);
        }

        this.dmSitesLoading.set(false);
      },
      error: (err) => {
        console.error(`Failed to load DM sites for country ${countryId}`, err);
        this.clearDmSite();
        this.dmSitesLoading.set(false);
      },
    });
  }

  private extractDmSiteRows(response: any): any[] {
    const data = response?.data ?? response;
    if (Array.isArray(data?.rows)) return data.rows;
    if (Array.isArray(data)) return data;
    return [];
  }

  private getCountryOptionsForRegion(regionId: string | number | null): SelectOption[] {
    if (regionId === null || regionId === '') return [];

    if (!this.regionsWithCountries.length) {
      return this.allCountryOptions;
    }

    const region = this.regionsWithCountries.find((r: any) => this.sameValue(r.id, regionId));
    return this.mapValidCountries(region?.countries || []);
  }

  onFallbackCountrySearch = (query: string): void => {
    const trimmedQuery = (query || '').trim();
    this.fetchFallbackCountries(this.selectedRegion(), trimmedQuery || undefined);
  };

  onFallbackCountryDropdownOpened(): void {
    if (
      this.regionUsesFallbackCountries(this.selectedRegion()) &&
      !this.fallbackCountriesLoading() &&
      this.fallbackCountryOptions().length === 0
    ) {
      this.fetchFallbackCountries(this.selectedRegion());
    }
  }

  private regionUsesFallbackCountries(regionId: string | number | null): boolean {
    if (regionId === null || regionId === '' || !this.regionsWithCountries.length) return false;

    const region = this.getRegionById(regionId);
    if (!region) return false;

    return this.mapValidCountries(region?.countries || []).length === 0;
  }

  private fetchFallbackCountries(
    regionId: string | number | null,
    searchQuery?: string
  ): void {
    if (regionId === null || regionId === '') {
      this.fallbackCountryOptions.set([]);
      this.fallbackCountriesLoading.set(false);
      return;
    }

    const requestId = ++this.fallbackCountriesRequestId;
    const payload: any = searchQuery ? { name: searchQuery } : { page: 1, limit: 10 };

    if (!this.isCorpRegion(regionId)) {
      payload.region_id = this.toApiId(regionId);
    }

    this.fallbackCountriesLoading.set(true);
    this.apiService.sendRequest(requests.getAllCountries, 'post', payload).subscribe({
      next: (res: any) => {
        if (requestId !== this.fallbackCountriesRequestId || !this.sameValue(this.selectedRegion(), regionId)) {
          return;
        }

        const options = this.mapValidCountries(this.extractCountryRows(res));
        const selectedCountry = this.selectedCountry();
        const selectedOption = this.fallbackCountryOptions().find((option) =>
          this.sameValue(option.value, selectedCountry)
        );

        const nextOptions = this.withSelectedOption(options, selectedOption);
        this.fallbackCountryOptions.set(nextOptions);

        this.fallbackCountriesLoading.set(false);
      },
      error: (err) => {
        if (requestId !== this.fallbackCountriesRequestId) return;
        console.error(`Failed to load countries for region ${regionId}`, err);
        this.fallbackCountryOptions.set([]);
        this.fallbackCountriesLoading.set(false);
      }
    });
  }

  private getRegionById(regionId: string | number): any | undefined {
    return this.regionsWithCountries.find((region: any) => this.sameValue(region.id, regionId));
  }

  private isCorpRegion(regionId: string | number): boolean {
    const region = this.getRegionById(regionId);
    return String(region?.name ?? '').trim().toUpperCase() === 'CORP';
  }

  private extractCountryRows(response: any): any[] {
    const data = response?.data ?? response;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.rows)) return data.rows;
    return [];
  }

  private withSelectedOption(options: SelectOption[], selectedOption: SelectOption | undefined): SelectOption[] {
    if (!selectedOption || options.some((option) => this.sameValue(option.value, selectedOption.value))) {
      return options;
    }

    return [...options, selectedOption];
  }

  private toApiId(value: string | number): string | number {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : value;
  }

  private mapValidCountries(countries: any[]): SelectOption[] {
    return countries
      .filter((c: any) => c?.id != null && c?.name)
      .map((c: any) => ({ value: c.id, label: c.name }));
  }

  private sameValue(left: string | number | null, right: string | number | null): boolean {
    return left === right || String(left) === String(right);
  }

  toggleMobileMenu(): void {
    this.layoutService.toggleMobileMenu();
  }

  get showMobileMenu() {
    return this.layoutService.mobileMenuOpen;
  }

  toggleUserMenu(): void {
    this.showUserMenu.update(val => !val);
  }

  toggleNotifications(): void {
    this.showNotifications.update(val => !val);
  }

  closeMenus(): void {
    this.showUserMenu.set(false);
    this.showNotifications.set(false);
  }

  getGreeting(): string {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.user-menu-container') && !target.closest('.notifications-container')) {
      this.closeMenus();
    }
  }

  logout(): void {
    this.authService.logout();
  }
}
